import type { EdgeChartApi } from '@edge/charts'
import type { ActionCallbacks } from '@/features/companion/types'
import { pickPanel } from '@/features/companion/last-panel-tracker'
import {
  chartCellIds,
  chartPanelIds,
  getChartApi,
  getChartCellApi,
  getChartControlTarget,
  getChartDrawingsStore,
} from '@/workspace/chart-api-registry'
import type { ChartDrawingsStore } from '../chart-assets-store'

/**
 * The chart a companion chart tool acts on: the one named, else the on-screen
 * chart the user last pressed into, else the first on-screen chart. Light on
 * purpose: action-snapshot.ts resolves the same panel before a call runs.
 */
export function resolveChartPanelId(
  callbacks: ActionCallbacks,
  requested?: unknown,
): string | null {
  if (typeof requested === 'string' && requested) {
    if (getChartApi(requested)) return requested
    // A tab group's host or tab (what the user picks on screen): the chart
    // showing is the group's active tab.
    const active = callbacks.activePanelOf?.(requested)
    return active && getChartApi(active) ? active : requested
  }
  const charts = (callbacks.getPanelsByType?.('chart') ?? []).map((c) => c.panelId)
  return pickPanel(
    'chart',
    charts.filter((id) => getChartApi(id)),
  )
}

/** The grid panel a cell id (`panelId::i`) belongs to, or null when it is not a cell. */
function panelOfCell(cellId: string): string | null {
  for (const panelId of chartPanelIds())
    if (panelId !== cellId && chartCellIds(panelId).includes(cellId)) return panelId
  return null
}

/**
 * Where a chart tool's change lands, as the chart's own header writes it. A 1×1
 * chart is its panel. In a grid each cell holds its studies, so they go to the
 * named cell, else the active one; the timeframe too, unless interval sync
 * makes it the panel's. action-snapshot.ts snapshots these same keys.
 */
export interface ChartWriteTarget {
  panelId: string
  /** The cell's state key: studies, and the bars and drawings read from it. */
  cellId: string
  /** Where the timeframe is written: the cell, or the panel under interval sync. */
  periodId: string
}

export function resolveChartWriteTarget(
  callbacks: ActionCallbacks,
  requested?: unknown,
): ChartWriteTarget | null {
  const named = typeof requested === 'string' && requested ? panelOfCell(requested) : null
  const panelId = named ?? resolveChartPanelId(callbacks, requested)
  if (!panelId) return null
  const control = getChartControlTarget(panelId)
  const cellId = named ? (requested as string) : (control?.activeCellId ?? panelId)
  return { panelId, cellId, periodId: control?.intervalSynced ? panelId : cellId }
}

export interface ChartTarget extends ChartWriteTarget {
  api: EdgeChartApi
  store: ChartDrawingsStore
  /** The ticker the chart shows, which keys its drawings in the store. */
  ticker: string
}

export function chartTarget(
  callbacks: ActionCallbacks,
  requested?: unknown,
): ChartTarget | { error: string } {
  const target = resolveChartWriteTarget(callbacks, requested)
  if (!target)
    return { error: 'No chart is open on this page. Add one with add_panel, then try again.' }
  const { panelId, cellId } = target
  const api = getChartCellApi(panelId, cellId) ?? getChartApi(panelId)
  const store = getChartDrawingsStore(panelId)
  if (!api || !store) return { error: `Chart ${panelId} is not on screen.` }
  const ticker = api.getSymbol().ticker
  if (!ticker) return { error: `Chart ${panelId} has no ticker yet.` }
  return { ...target, api, store, ticker }
}
